import React from 'react';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import type { SingleVersion } from '@atlassian/jira-version-picker/src/common/types.tsx';
import MultiVersionPicker from '@atlassian/jira-version-picker/src/view/multi/index.tsx';
import type { FieldConfigurationComponentProps } from '../../../../../types.tsx';
import type { AssociatedField } from '../../../../../types/layout-items.tsx';
import TestIdWrapper from '../../../common/test-wrapper/index.tsx';
import { AkFieldWrapper, FieldConfigurationDefaultValue } from '../../../styled.tsx';
import { refineDefaultValue } from '../common/utils.tsx';
import messages from './messages.tsx';

const onDefaultValueChange =
	(field: AssociatedField, onUpdate: (updatedField: AssociatedField) => void) =>
	(updatedValue: SingleVersion[]) =>
		onUpdate({
			...field,
			defaultValue: updatedValue.length === 0 ? null : updatedValue,
		});

const toListOfVersions = (defaultValue: unknown): SingleVersion[] =>
	Array.isArray(defaultValue) ? defaultValue.map(refineDefaultValue).filter(Boolean) : [];

export const MultiVersionFieldConfiguration = ({
	field,
	onUpdate,
	projectKey,
	tooltipDelay,
	testId,
}: FieldConfigurationComponentProps) => {
	const { formatMessage } = useIntl();
	const currentValue = toListOfVersions(field.defaultValue);

	const defaultLabel = messages.multiVersionDefaultValueLabelNew;
	const placeholder = messages.multiVersionPlaceholder;
	const disabledTooltipMessage = messages.multiVersionDefaultValueDisabledTooltip;
	const isModifyDefaultValueDisabled = !field.operations.canModifyDefaultValue;

	return (
		<FieldConfigurationDefaultValue>
			<AkFieldWrapper>
				<Tooltip
					content={isModifyDefaultValueDisabled ? formatMessage(disabledTooltipMessage) : null}
					delay={tooltipDelay}
				>
					<TestIdWrapper testId={testId}>
						<MultiVersionPicker
							value={currentValue}
							label={formatMessage(defaultLabel)}
							placeholder={formatMessage(placeholder)}
							projectKey={projectKey}
							onSelected={onDefaultValueChange(field, onUpdate)}
							isDisabled={isModifyDefaultValueDisabled}
							// This picker is intended for selecting default values
							// therefore it's meant for future issues where archived versions do not make sense.
							// If this was a search page or reports page then we would include archived versions too.
							excludeArchivedVersions
							// this is used to prevent the menu from remaining
							// open when a user clicks through to another field
							noPortal
							isClearableWithCrossIcon
						/>
					</TestIdWrapper>
				</Tooltip>
			</AkFieldWrapper>
		</FieldConfigurationDefaultValue>
	);
};
